class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    const closeButton = this.querySelector('button[type="button"]');
    const resetButton = this.querySelector('button[type="reset"]');

    form?.addEventListener('submit', this.onFormSubmit.bind(this));
    closeButton?.addEventListener('click', this.close.bind(this));
    resetButton?.addEventListener('click', this.clear.bind(this));

    this.input?.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input?.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keydown', this.onTabKey.bind(this));
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  updatePopularSearches() {
    this.classList.toggle('ae-popular-open', !this.getQuery().length && this.contains(document.activeElement));
  }

  onChange() {
    this.updatePopularSearches();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    this.updatePopularSearches();
    if (this.getQuery().length) document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) { this.close(); this.classList.remove('ae-popular-open'); }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  onTabKey(event) {
    if (!this.getAttribute('open') || event.key !== 'Tab') return;
    const allLinks = Array.from(this.querySelectorAll('li[aria-selected] a, li[aria-selected] button'));
    if (!allLinks.length) return;

    const currentIndex = allLinks.indexOf(document.activeElement);
    const nextIndex = event.shiftKey
      ? (currentIndex - 1 + allLinks.length) % allLinks.length
      : (currentIndex + 1) % allLinks.length;

    event.preventDefault();
    allLinks.forEach((link) => {
      link.setAttribute('tabindex', '-1');
      link.closest('li')?.setAttribute('aria-selected', 'false');
    });

    const activeLink = allLinks[nextIndex];
    const activeLi = activeLink.closest('li');
    activeLink.setAttribute('tabindex', '0');
    activeLink.focus();
    activeLi?.setAttribute('aria-selected', 'true');
    this.input?.setAttribute('aria-activedescendant', activeLi?.id || '');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const allElements = Array.from(this.predictiveSearchResults.querySelectorAll('li[role="option"]'));
    const selectedElement = this.querySelector('[aria-selected="true"]');
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = allElements[(allElements.indexOf(selectedElement) + 1) % allElements.length];
    } else if (moveUp) {
      activeElement = allElements[(allElements.indexOf(selectedElement) - 1 + allElements.length) % allElements.length];
    }

    if (!activeElement || activeElement === selectedElement) return;

    allElements.forEach((li) => {
      li.setAttribute('aria-selected', 'false');
      li.querySelector('a, button')?.setAttribute('tabindex', '-1');
    });

    activeElement.setAttribute('aria-selected', 'true');
    const activeLink = activeElement.querySelector('a, button');
    activeLink?.setAttribute('tabindex', '0');
    activeLink?.focus();
    this.setLiveRegionText(activeElement.textContent);
    this.input?.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')?.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.modal?.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search markup missing');
        const resultsMarkup = section.innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        if (this.getQuery() !== searchTerm) return;
        this.renderSearchResults(resultsMarkup);
        this.modal?.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return;
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    if (this.statusElement) this.setLiveRegionText(this.loadingText || 'Loading');
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const productGroup = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list')?.closest('.predictive-search__result-group');
    const wrapper = this.predictiveSearchResults.querySelector('.predictive-search__results-groups-wrapper');
    if (productGroup && wrapper) { wrapper.prepend(productGroup); wrapper.classList.add('ae-products-first'); }
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const products = this.querySelectorAll('#predictive-search-results-products-list > li').length;
    const suggestions = this.querySelectorAll('#predictive-search-results-queries-list > li').length;
    const count = `${products} products and ${suggestions} suggestions for ${this.getQuery()}`;
    this.setLiveRegionText(count);
  }

  getResultsMaxHeight() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const headerBottom = document.querySelector('.header')?.getBoundingClientRect().bottom || 0;
    this.resultsMaxHeight = viewportHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input?.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event?.preventDefault?.();
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.searchTerm = '';
    if (this.statusElement) this.statusElement.textContent = '';
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.isOpen = false;
    document.body.classList.remove('predictive-search--focus');
    this.modal?.classList.remove('searching');
    this.updatePopularSearches();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

/* Aqua Essentials draft desktop header upgrade */
(() => {
  const desktopQuery = window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)');

  const injectStyles = () => {
    if (document.getElementById('ae-desktop-header-upgrade-styles')) return;
    const style = document.createElement('style');
    style.id = 'ae-desktop-header-upgrade-styles';
    style.textContent = `
      .shopify-section-header .ae-desktop-account-link { display:none; }
      @media screen and (max-width:749px) {
        .shopify-section-header .header__right .header__icon { width:44px; min-width:44px; }
      }
      @media screen and (max-width:374px) {
        .shopify-section-header .header { grid-template-columns:44px minmax(0,1fr) 176px; }
        .shopify-section-header .header__heading-link { min-width:0; max-width:100%; }
        .shopify-section-header .header__heading-logo { max-width:100%; height:auto; }
      }
      @media screen and (min-width:1200px) and (hover:hover) and (pointer:fine) {
        .shopify-section-header .header-wrapper,
        .shopify-section-header .header-transparent,
        .shopify-section-header .header-background { background:#fff !important; color:#253129 !important; }
        .shopify-section-header .header-transparent .header__menu-item--top,
        .shopify-section-header .header-transparent .header__icon,
        .shopify-section-header .header-transparent .header__heading-link .h2 { color:#253129 !important; }
        .shopify-section-header .header-transparent .header__heading-logo.normal { opacity:1 !important; }
        .shopify-section-header .header-transparent .header__heading-logo.inverse { opacity:0 !important; }

        .shopify-section-header .ae-desktop-account-link { display:inline-flex; min-height:42px; align-items:center; gap:7px; padding:0 4px; color:#253129; text-decoration:none; }
        .shopify-section-header .ae-desktop-account-link .icon { width:20px; height:20px; }
        .shopify-section-header .ae-desktop-account-link__label { font-size:12px; font-weight:700; white-space:nowrap; }
        .shopify-section-header shopify-account.header__icon--account { display:none !important; }

        .shopify-section-header.shopify-section-header-hidden { transform:none !important; }
        .shopify-section-header.shopify-section-header-sticky { transform:none !important; background:#fff !important; box-shadow:0 5px 18px rgba(37,49,41,.09); }
        .shopify-section-header.shopify-section-header-sticky .ae-desktop-announcement { display:none !important; }
        .shopify-section-header.shopify-section-header-sticky .header-wrapper { position:relative !important; transform:none !important; }
        .shopify-section-header.shopify-section-header-sticky .header {
          width:min(1368px,calc(100% - 64px));
          min-height:60px;
          padding:0 !important;
          display:grid;
          grid-template-columns:150px minmax(0,1fr) auto;
          grid-template-areas:'logo nav actions';
          column-gap:18px;
          align-items:center;
        }
        .shopify-section-header.shopify-section-header-sticky .header__heading,
        .shopify-section-header.shopify-section-header-sticky .header > .header__heading-link { grid-area:logo; }
        .shopify-section-header.shopify-section-header-sticky .header__heading-logo { max-width:138px !important; max-height:48px !important; }
        .shopify-section-header.shopify-section-header-sticky .ae-desktop-header-search { display:none !important; }
        .shopify-section-header.shopify-section-header-sticky .ae-desktop-plant-finder { display:none !important; }
        .shopify-section-header.shopify-section-header-sticky .header__inline-menu {
          grid-area:nav;
          margin:0 !important;
          padding:0 !important;
          border-top:0 !important;
          min-width:0;
        }
        .shopify-section-header.shopify-section-header-sticky .header__inline-menu .list-menu--inline {
          justify-content:space-between;
          column-gap:10px;
        }
        .shopify-section-header.shopify-section-header-sticky .header__menu-item { padding-block:19px !important; font-size:10.5px !important; }
        .shopify-section-header.shopify-section-header-sticky .header__right { grid-area:actions; gap:3px; }
        .shopify-section-header.shopify-section-header-sticky .header__right > search-modal { display:block !important; }
        .shopify-section-header.shopify-section-header-sticky .header__right > search-modal .header__icon { color:#253129 !important; }
        .shopify-section-header.shopify-section-header-sticky .header__right > cart-drawer > details > summary,
        .shopify-section-header.shopify-section-header-sticky .header__right > a.header__icon--cart { min-width:42px; }
        .shopify-section-header.shopify-section-header-sticky::after { content:''; display:block; height:0; }
        body.shopify-body-section-header-sticky .transition-content { padding-top:0 !important; }
      }
    `;
    document.head.appendChild(style);
  };

  const fixNavigation = () => {
    const header = document.querySelector('.shopify-section-header .header');
    if (!header) return;
    const items = header.querySelectorAll('.header__inline-menu .list-menu--inline > li');
    const moreProducts = items[6];
    if (moreProducts) {
      const link = moreProducts.querySelector(':scope > * > .header__menu-item--top');
      const label = link?.querySelector('.label');
      if (label) label.textContent = 'More Products';
      if (link?.dataset?.aeOriginalHref !== undefined) link.setAttribute('href', link.dataset.aeOriginalHref || '#');
      moreProducts.classList.remove('ae-desktop-locked-simple');
    }
  };

  const fixAccount = () => {
    const headerRight = document.querySelector('.shopify-section-header .header__right');
    const nativeAccount = headerRight?.querySelector('shopify-account.header__icon--account');
    if (!headerRight || !nativeAccount || headerRight.querySelector('.ae-desktop-account-link')) return;

    const link = document.createElement('a');
    link.href = '/account';
    link.className = 'ae-desktop-account-link focus-inset';
    link.setAttribute('aria-label', 'Account');
    const icon = nativeAccount.querySelector('svg')?.cloneNode(true);
    if (icon) link.appendChild(icon);
    const label = document.createElement('span');
    label.className = 'ae-desktop-account-link__label';
    label.textContent = 'Account';
    link.appendChild(label);
    nativeAccount.insertAdjacentElement('afterend', link);
  };

  const apply = () => {
    injectStyles();
    if (!desktopQuery.matches) return;
    fixNavigation();
    fixAccount();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply, { once:true });
  else apply();
})();
